<div class="settings-container">
  <!-- Category Tabs — flat bar at the very top (mirrors the Channels tab bar) -->
  <div class="category-tabs" *ngIf="!isLoading">
    <div class="tabs-container">
      <ng-container *ngFor="let node of categoryTree">
        <button
          class="tab-button"
          [class.active]="isCategorySelected(node.category)"
          (click)="selectCategory(node.category)"
        >
          <i class="fa-solid {{ node.category.Icon || 'fa-folder' }}"></i>
          <span>{{ node.category.Name }}</span>
        </button>
        <!-- Show child categories as sub-tabs if this parent is selected -->
        <ng-container *ngIf="isParentOfSelected(node) && node.children.length > 0">
          <div class="sub-tabs">
            <button
              *ngFor="let child of node.children"
              class="sub-tab-button"
              [class.active]="isCategorySelected(child.category)"
              (click)="selectCategory(child.category)"
            >
              <i class="fa-solid {{ child.category.Icon || 'fa-folder' }}"></i>
              <span>{{ child.category.Name }}</span>
            </button>
          </div>
        </ng-container>
      </ng-container>
    </div>
    <div class="tab-indicator"></div>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Loading settings...</span>
  </div>

  <!-- Error Message -->
  <div class="error-banner" *ngIf="errorMessage">
    <i class="fa-solid fa-exclamation-triangle"></i>
    {{ errorMessage }}
    <button class="close-btn" (click)="errorMessage = ''">
      <i class="fa-solid fa-times"></i>
    </button>
  </div>


  <!-- Main Content -->
  <div class="settings-content" *ngIf="!isLoading">
    <!-- Settings Panel -->
    <div class="settings-panel">
      <!-- Category Info -->
      <div class="category-header" *ngIf="selectedCategory">
        <div class="category-info">
          <i class="fa-solid {{ selectedCategory.Icon || 'fa-folder' }}"></i>
          <div class="category-details">
            <h3>{{ selectedCategory.Name }}</h3>
            <p *ngIf="selectedCategory.Description">{{ selectedCategory.Description }}</p>
          </div>
        </div>
        <div class="header-actions">
          <div class="save-status" [class.visible]="saveStatusVisible" [class.error]="saveStatusError">
            <i class="fa-solid" [class.fa-check-circle]="!saveStatusError" [class.fa-exclamation-circle]="saveStatusError"></i>
            <span>{{ saveStatusMessage }}</span>
          </div>
          <div class="search-box">
            <i class="fa-solid fa-search"></i>
            <input
              type="text"
              placeholder="Search settings..."
              [(ngModel)]="searchQuery"
            />
            <button class="clear-btn" *ngIf="searchQuery" (click)="searchQuery = ''">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <button
            class="help-hint-btn"
            *ngIf="showHierarchyHelpHint"
            (click)="toggleHierarchyHelp()"
            [class.active]="hierarchyHelpExpanded"
            title="How do these settings work across channels and organizations?"
          >
            <i class="fa-solid fa-circle-info"></i>
            <span>How settings inherit</span>
            <i class="fa-solid" [class.fa-chevron-down]="!hierarchyHelpExpanded" [class.fa-chevron-up]="hierarchyHelpExpanded"></i>
          </button>
          <span class="settings-count">{{ filteredSettings.length }} setting(s)</span>
        </div>
      </div>

      <!-- Settings Hierarchy Help Panel -->
      <div class="hierarchy-help-panel" *ngIf="hierarchyHelpExpanded && showHierarchyHelpHint">
        <div class="help-content">
          <h4><i class="fa-solid fa-sitemap"></i> Settings Inheritance</h4>
          <p class="help-intro">
            Settings are resolved in order of specificity. More specific settings override inherited ones.
          </p>

          <div class="inheritance-flow">
            <div class="flow-item" [class.active]="hierarchyInfo.channelCount > 1">
              <div class="flow-icon"><i class="fa-solid fa-broadcast-tower"></i></div>
              <div class="flow-label">Channel</div>
              <div class="flow-desc">Per-channel overrides</div>
              <div class="flow-status" *ngIf="hierarchyInfo.channelCount > 1">
                {{ hierarchyInfo.channelCount }} channels
              </div>
            </div>
            <div class="flow-arrow"><i class="fa-solid fa-arrow-left"></i></div>
            <div class="flow-item active">
              <div class="flow-icon"><i class="fa-solid fa-building"></i></div>
              <div class="flow-label">Organization</div>
              <div class="flow-desc">{{ selectedOrganization?.Name }}</div>
            </div>
            <ng-container *ngIf="hierarchyInfo.hasParentOrg">
              <div class="flow-arrow"><i class="fa-solid fa-arrow-left"></i></div>
              <div class="flow-item">
                <div class="flow-icon"><i class="fa-solid fa-building-circle-arrow-right"></i></div>
                <div class="flow-label">Parent Org</div>
                <div class="flow-desc">{{ hierarchyInfo.parentOrgName }}</div>
              </div>
            </ng-container>
            <div class="flow-arrow"><i class="fa-solid fa-arrow-left"></i></div>
            <div class="flow-item">
              <div class="flow-icon"><i class="fa-solid fa-globe"></i></div>
              <div class="flow-label">Default</div>
              <div class="flow-desc">System defaults</div>
            </div>
          </div>

          <div class="help-examples">
            <div class="example" *ngIf="hierarchyInfo.channelCount > 1">
              <i class="fa-solid fa-lightbulb"></i>
              <span>
                <strong>Per-channel settings:</strong> You can enable auto-approve for your email channel
                while keeping it disabled for chat, allowing different approval workflows per channel.
              </span>
            </div>
            <div class="example" *ngIf="hierarchyInfo.hasParentOrg">
              <i class="fa-solid fa-lightbulb"></i>
              <span>
                <strong>Inherited from parent:</strong> Settings not customized here will inherit from
                <em>{{ hierarchyInfo.parentOrgName }}</em>. Set values here to override the parent's configuration.
              </span>
            </div>
            <div class="example" *ngIf="hierarchyInfo.hasChildOrgs">
              <i class="fa-solid fa-lightbulb"></i>
              <span>
                <strong>Inherited by children:</strong> {{ hierarchyInfo.childOrgCount }} child
                organization{{ hierarchyInfo.childOrgCount > 1 ? 's' : '' }} will inherit settings
                configured here unless they have their own overrides.
              </span>
            </div>
          </div>
        </div>
        <button class="close-help-btn" (click)="toggleHierarchyHelp()">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <!-- Settings List -->
      <div class="settings-list" *ngIf="selectedCategory">

        <!-- Website URL field (Website Sync category only) -->
        <div class="setting-card website-url-card" *ngIf="isWebsiteSyncCategory && selectedOrganization">
          <div class="setting-header">
            <div class="setting-info">
              <i class="fa-solid fa-globe"></i>
              <div class="setting-details">
                <h4>Website URL</h4>
                <p>The URL Izzy crawls to keep your knowledge base up to date.</p>
              </div>
            </div>
            <div class="setting-badges">
              <span class="badge type-badge">URL</span>
              <span class="badge override-badge" *ngIf="selectedOrganization.Website">
                <i class="fa-solid fa-pen"></i> Customized
              </span>
              <span class="badge default-badge" *ngIf="!selectedOrganization.Website">
                <i class="fa-solid fa-star"></i> Default
              </span>
            </div>
          </div>
          <div class="setting-value">
            <div class="value-display" *ngIf="!isEditingWebsiteUrl">
              <span class="current-value" [class.is-default]="!selectedOrganization.Website">
                {{ selectedOrganization.Website || '(not set)' }}
              </span>
              <button class="btn-edit" (click)="startEditingWebsiteUrl()">
                <i class="fa-solid fa-pen"></i> Edit
              </button>
            </div>
            <div class="value-editor" *ngIf="isEditingWebsiteUrl">
              <input
                type="url"
                class="setting-input"
                [(ngModel)]="websiteUrlEditValue"
                placeholder="https://example.com"
                (keydown.enter)="saveWebsiteUrl()"
                (keydown.escape)="cancelEditingWebsiteUrl()"
              />
              <div class="edit-actions">
                <button class="btn-save" (click)="saveWebsiteUrl()" [disabled]="isSavingWebsiteUrl">
                  <i class="fa-solid fa-check"></i> {{ isSavingWebsiteUrl ? 'Saving...' : 'Save' }}
                </button>
                <button class="btn-cancel" (click)="cancelEditingWebsiteUrl()" [disabled]="isSavingWebsiteUrl">
                  <i class="fa-solid fa-times"></i> Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="empty-state" *ngIf="filteredSettings.length === 0">
          <i class="fa-solid fa-cog"></i>
          <p *ngIf="searchQuery">No settings match your search</p>
          <p *ngIf="!searchQuery">No settings in this category</p>
        </div>

        <div
          class="setting-card"
          *ngFor="let sd of filteredSettings"
          [class.is-overridden]="sd.isOverridden"
          [class.is-editing]="sd.isEditing"
          [class.is-disabled]="isSettingDisabled(sd)"
        >
          <div class="setting-header">
            <div class="setting-info">
              <i class="fa-solid {{ getSettingIcon(sd.setting) }}"></i>
              <div class="setting-details">
                <h4>{{ sd.setting.Name }}</h4>
                <p *ngIf="sd.setting.Description">{{ sd.setting.Description }}</p>
                <p class="plan-gate-message" *ngIf="isDisabledByPlan(sd)">
                  <i class="fa-solid fa-lock"></i> {{ getPlanGateMessage(sd) }}
                </p>
              </div>
            </div>
            <div class="setting-badges">
              <span class="badge type-badge">{{ getEffectiveValueType(sd.setting) }}</span>
              <span class="badge override-badge" *ngIf="sd.isOverridden">
                <i class="fa-solid fa-pen"></i> Customized
              </span>
              <span class="badge default-badge" *ngIf="!sd.isOverridden">
                <i class="fa-solid fa-star"></i> Default
              </span>
              <!-- Channel overrides indicator -->
              <span class="badge channel-badge" *ngIf="sd.channelOverrides.length > 0"
                    (click)="toggleChannelOverrides(sd)"
                    title="Click to view channel-specific overrides">
                <i class="fa-solid fa-broadcast-tower"></i>
                {{ sd.channelOverrides.length }} channel{{ sd.channelOverrides.length > 1 ? 's' : '' }}
              </span>
              <!-- Org-only indicator -->
              <span class="badge scope-badge org-only" *ngIf="isOrgOnlySetting(sd)" title="This setting applies organization-wide only">
                <i class="fa-solid fa-building"></i> Org only
              </span>
              <!-- Channel-only indicator -->
              <span class="badge scope-badge channel-only" *ngIf="isChannelOnlySetting(sd)" title="This setting is configured per-channel">
                <i class="fa-solid fa-broadcast-tower"></i> Per channel
              </span>
            </div>
          </div>

          <!-- Organization-level value editor (only for org-scoped settings) -->
          <div class="setting-value" *ngIf="!isChannelOnlySetting(sd)">
            <!-- Boolean Toggle -->
            <div class="value-control boolean-control" *ngIf="isBoolean(sd) && !sd.isEditing">
              <label class="toggle-switch" [class.disabled]="isSettingDisabled(sd)">
                <input
                  type="checkbox"
                  [checked]="getBooleanValue(sd)"
                  (change)="toggleBooleanSetting(sd)"
                  [disabled]="isSaving || isSettingDisabled(sd)"
                />
                <span class="toggle-slider"></span>
              </label>
              <span class="value-text">{{ getBooleanValue(sd) ? 'Enabled' : 'Disabled' }}</span>
            </div>

            <!-- Number Input -->
            <div class="value-control number-control" *ngIf="isNumber(sd) && !sd.isEditing">
              <span class="current-value">{{ sd.currentValue || 'Not set' }}</span>
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-edit"></i>
              </button>
            </div>

            <!-- JSON View -->
            <div class="value-control json-control" *ngIf="isJSON(sd) && !sd.isEditing">
              <pre class="code-preview">{{ sd.currentValue || '{}' }}</pre>
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-edit"></i>
              </button>
            </div>

            <!-- Markdown View -->
            <div class="value-control markdown-control" *ngIf="isMarkdown(sd) && !sd.isEditing">
              <div class="markdown-rendered" [class.clamped]="!sd.previewExpanded" [innerHTML]="renderMarkdown(sd.currentValue)"></div>
              <button class="preview-toggle" *ngIf="(sd.currentValue || '').length > 400" (click)="sd.previewExpanded = !sd.previewExpanded">
                {{ sd.previewExpanded ? 'Show less' : 'Show more' }}
                <i class="fa-solid" [ngClass]="sd.previewExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </button>
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-edit"></i>
              </button>
            </div>

            <!-- HTML View -->
            <div class="value-control html-control" *ngIf="isHTML(sd) && !sd.isEditing">
              <div class="html-preview" [innerHTML]="sd.currentValue || ''"></div>
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-edit"></i>
              </button>
            </div>

            <!-- List Dropdown -->
            <div class="value-control list-control" *ngIf="isList(sd) && !sd.isEditing">
              <select
                class="list-select"
                [ngModel]="sd.currentValue"
                (ngModelChange)="setListValue(sd, $event)"
                [disabled]="isSaving || isSettingDisabled(sd)">
                <option *ngFor="let opt of getListOptions(sd)" [value]="opt">{{ humanizeOption(opt) }}</option>
              </select>
            </div>

            <!-- MultiList Chips -->
            <div class="value-control multilist-control" *ngIf="isMultiList(sd) && !sd.isEditing">
              <button
                *ngFor="let opt of getListOptions(sd)"
                type="button"
                class="multilist-chip"
                [class.selected]="isMultiListSelected(sd, opt)"
                (click)="toggleMultiListValue(sd, opt)"
                [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid" [class.fa-check]="isMultiListSelected(sd, opt)" [class.fa-plus]="!isMultiListSelected(sd, opt)"></i>
                {{ humanizeOption(opt) }}
              </button>
            </div>

            <!-- String View (default) -->
            <div class="value-control string-control" *ngIf="!isBoolean(sd) && !isNumber(sd) && !isRichType(sd) && !isList(sd) && !isMultiList(sd) && !sd.isEditing">
              <span class="current-value">{{ sd.currentValue || 'Not set' }}</span>
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-edit"></i>
              </button>
            </div>

            <!-- Edit Mode -->
            <div class="value-edit" *ngIf="sd.isEditing">
              <!-- JSON/Markdown Editor (using code editor) -->
              <mj-code-editor
                *ngIf="isJSON(sd) || isMarkdown(sd)"
                [(ngModel)]="sd.editValue"
                [language]="isJSON(sd) ? 'json' : 'markdown'"
                [lineWrapping]="true"
                [placeholder]="isJSON(sd) ? 'Enter JSON...' : 'Enter Markdown...'"
                class="rich-editor"
              ></mj-code-editor>

              <!-- HTML Editor (textarea for now, can upgrade to WYSIWYG later) -->
              <textarea
                *ngIf="isHTML(sd)"
                [(ngModel)]="sd.editValue"
                rows="10"
                class="html-editor"
                placeholder="Enter HTML..."
              ></textarea>

              <!-- Number Input -->
              <input
                *ngIf="isNumber(sd)"
                type="number"
                [(ngModel)]="sd.editValue"
              />

              <!-- String Editor (default) - use code editor for multiline -->
              <mj-code-editor
                *ngIf="!isBoolean(sd) && !isNumber(sd) && !isRichType(sd)"
                [(ngModel)]="sd.editValue"
                [language]="'markdown'"
                [lineWrapping]="true"
                placeholder="Enter value..."
                class="string-editor"
              ></mj-code-editor>

              <div class="edit-actions">
                <button class="save-btn" (click)="saveSettingValue(sd)" [disabled]="isSaving">
                  <i class="fa-solid fa-check"></i> Save
                </button>
                <button class="cancel-btn" (click)="cancelEditing(sd)" [disabled]="isSaving">
                  <i class="fa-solid fa-times"></i> Cancel
                </button>
              </div>
            </div>
          </div>

          <div class="setting-footer" *ngIf="sd.isOverridden && !sd.isEditing && !isChannelOnlySetting(sd)">
            <span class="default-value">
              Default: <code>{{ sd.setting.DefaultValue || 'Not set' }}</code>
            </span>
            <div class="footer-actions">
              <button class="edit-btn" (click)="startEditing(sd)" [disabled]="isSaving || isSettingDisabled(sd)" *ngIf="!isBoolean(sd)">
                <i class="fa-solid fa-edit"></i> Edit
              </button>
              <button class="revert-btn" (click)="revertToDefault(sd)" [disabled]="isSaving || isSettingDisabled(sd)">
                <i class="fa-solid fa-undo"></i> Revert to default
              </button>
            </div>
          </div>

          <!-- Channel Values Section -->
          <!-- For channel-only settings: always shown, always expanded, different messaging -->
          <!-- For org+channel settings: collapsible, shown as "overrides" -->
          <div class="channel-overrides-section" [class.channel-only-mode]="isChannelOnlySetting(sd)"
               *ngIf="(showChannelOverrideUI(sd) || isChannelOnlySetting(sd)) && !isSettingDisabled(sd)">
            <!-- Toggle button (only for org+channel settings, not channel-only) -->
            <button class="channel-overrides-toggle"
                    *ngIf="!isChannelOnlySetting(sd)"
                    (click)="toggleChannelOverrides(sd)"
                    [class.expanded]="sd.channelOverridesExpanded">
              <i class="fa-solid fa-broadcast-tower"></i>
              <span *ngIf="sd.channelOverrides.length === 0">Configure per-channel values</span>
              <span *ngIf="sd.channelOverrides.length > 0">
                {{ sd.channelOverrides.length }} channel override{{ sd.channelOverrides.length > 1 ? 's' : '' }}
              </span>
              <i class="fa-solid" [class.fa-chevron-down]="!sd.channelOverridesExpanded" [class.fa-chevron-up]="sd.channelOverridesExpanded"></i>
            </button>

            <!-- Channel values panel (always visible for channel-only, toggleable for org+channel) -->
            <div class="channel-overrides-panel" *ngIf="sd.channelOverridesExpanded || isChannelOnlySetting(sd)">
              <div class="panel-header">
                <span class="panel-title" *ngIf="!isChannelOnlySetting(sd)">Channel-specific values</span>
                <span class="panel-title" *ngIf="isChannelOnlySetting(sd)">Channel Values</span>
                <span class="panel-hint" *ngIf="!isChannelOnlySetting(sd)">Override the organization default for specific channels</span>
                <span class="panel-hint" *ngIf="isChannelOnlySetting(sd)">Configure this setting for each channel</span>
              </div>

              <!-- CHANNEL-ONLY MODE: Show all channels as a list -->
              <div class="channel-list" *ngIf="isChannelOnlySetting(sd) && orgChannels.length > 0">
                <div class="channel-list-item" *ngFor="let channel of orgChannels">
                  <div class="channel-info">
                    <i class="fa-solid fa-broadcast-tower"></i>
                    <span class="channel-name">{{ channel.Name }}</span>
                  </div>
                  <!-- Check if this channel has a value set -->
                  <ng-container *ngIf="getChannelOverrideValue(sd, channel.ID) as co; else noChannelValue">
                    <div class="channel-value" *ngIf="sd.editingChannelId !== channel.ID">
                      <!-- Boolean display -->
                      <ng-container *ngIf="isBoolean(sd)">
                        <label class="toggle-switch mini">
                          <input type="checkbox"
                                 [checked]="co.toLowerCase() === 'true'"
                                 (change)="toggleChannelBooleanOverride(sd, channel.ID)"
                                 [disabled]="isSaving" />
                          <span class="toggle-slider"></span>
                        </label>
                        <span class="value-label">{{ co.toLowerCase() === 'true' ? 'Enabled' : 'Disabled' }}</span>
                      </ng-container>
                      <!-- Non-boolean display -->
                      <ng-container *ngIf="!isBoolean(sd)">
                        <code class="value-preview">{{ co || 'Empty' }}</code>
                        <button class="edit-btn mini" (click)="startChannelEdit(sd, channel.ID)" [disabled]="isSaving">
                          <i class="fa-solid fa-edit"></i>
                        </button>
                      </ng-container>
                      <button class="clear-value-btn mini" (click)="clearChannelValue(sd, channel.ID)" [disabled]="isSaving"
                              title="Clear value">
                        <i class="fa-solid fa-eraser"></i>
                      </button>
                    </div>
                  </ng-container>
                  <ng-template #noChannelValue>
                    <div class="channel-value not-set" *ngIf="sd.editingChannelId !== channel.ID">
                      <!-- Boolean - show toggle even when not set -->
                      <ng-container *ngIf="isBoolean(sd)">
                        <label class="toggle-switch mini">
                          <input type="checkbox"
                                 [checked]="false"
                                 (change)="toggleChannelBooleanOverride(sd, channel.ID)"
                                 [disabled]="isSaving" />
                          <span class="toggle-slider"></span>
                        </label>
                        <span class="value-label not-set-label">Not set</span>
                      </ng-container>
                      <!-- Non-boolean - show set button -->
                      <ng-container *ngIf="!isBoolean(sd)">
                        <span class="not-set-label">Not set</span>
                        <button class="set-value-btn" (click)="startChannelEdit(sd, channel.ID)" [disabled]="isSaving">
                          <i class="fa-solid fa-pen"></i> Set value
                        </button>
                      </ng-container>
                    </div>
                  </ng-template>
                  <!-- Edit mode for this channel -->
                  <div class="channel-edit" *ngIf="sd.editingChannelId === channel.ID && !isBoolean(sd)">
                    <input *ngIf="isNumber(sd)" type="number" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" />
                    <input *ngIf="!isNumber(sd) && !isRichType(sd)" type="text" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" placeholder="Enter value..." />
                    <mj-code-editor *ngIf="isRichType(sd)"
                                   [(ngModel)]="sd.channelEditValue"
                                   [language]="isJSON(sd) ? 'json' : 'markdown'"
                                   [lineWrapping]="true"
                                   class="channel-rich-editor"></mj-code-editor>
                    <div class="channel-edit-actions">
                      <button class="save-btn mini" (click)="saveChannelOverride(sd, channel.ID)" [disabled]="isSaving">
                        <i class="fa-solid fa-check"></i>
                      </button>
                      <button class="cancel-btn mini" (click)="cancelChannelEdit(sd)" [disabled]="isSaving">
                        <i class="fa-solid fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- CHANNEL-ONLY MODE: No channels empty state -->
              <div class="no-channels-message" *ngIf="isChannelOnlySetting(sd) && orgChannels.length === 0">
                <i class="fa-solid fa-broadcast-tower"></i>
                <p>No channels configured</p>
                <span class="hint">This setting can only be configured after channels are created for this organization.</span>
              </div>

              <!-- NORMAL MODE: List of existing channel overrides + add buttons -->
              <ng-container *ngIf="!isChannelOnlySetting(sd)">
                <div class="channel-override-list" *ngIf="sd.channelOverrides.length > 0">
                  <div class="channel-override-item" *ngFor="let co of sd.channelOverrides">
                    <div class="channel-info">
                      <i class="fa-solid fa-broadcast-tower"></i>
                      <span class="channel-name">{{ co.channel.Name }}</span>
                    </div>
                    <div class="channel-value" *ngIf="sd.editingChannelId !== co.channel.ID">
                      <!-- Boolean display -->
                      <ng-container *ngIf="isBoolean(sd)">
                        <label class="toggle-switch mini">
                          <input type="checkbox"
                                 [checked]="co.value.toLowerCase() === 'true'"
                                 (change)="toggleChannelBooleanOverride(sd, co.channel.ID)"
                                 [disabled]="isSaving" />
                          <span class="toggle-slider"></span>
                        </label>
                        <span class="value-label">{{ co.value.toLowerCase() === 'true' ? 'Enabled' : 'Disabled' }}</span>
                      </ng-container>
                      <!-- Non-boolean display -->
                      <ng-container *ngIf="!isBoolean(sd)">
                        <code class="value-preview">{{ co.value || 'Empty' }}</code>
                        <button class="edit-btn mini" (click)="startChannelEdit(sd, co.channel.ID)" [disabled]="isSaving">
                          <i class="fa-solid fa-edit"></i>
                        </button>
                      </ng-container>
                      <button class="remove-btn mini" (click)="removeChannelOverride(sd, co.channel.ID)" [disabled]="isSaving"
                              title="Remove override (use org default)">
                        <i class="fa-solid fa-times"></i>
                      </button>
                    </div>
                    <!-- Edit mode for channel override -->
                    <div class="channel-edit" *ngIf="sd.editingChannelId === co.channel.ID && !isBoolean(sd)">
                      <input *ngIf="isNumber(sd)" type="number" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" />
                      <input *ngIf="!isNumber(sd) && !isRichType(sd)" type="text" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" />
                      <mj-code-editor *ngIf="isRichType(sd)"
                                     [(ngModel)]="sd.channelEditValue"
                                     [language]="isJSON(sd) ? 'json' : 'markdown'"
                                     [lineWrapping]="true"
                                     class="channel-rich-editor"></mj-code-editor>
                      <div class="channel-edit-actions">
                        <button class="save-btn mini" (click)="saveChannelOverride(sd, co.channel.ID)" [disabled]="isSaving">
                          <i class="fa-solid fa-check"></i>
                        </button>
                        <button class="cancel-btn mini" (click)="cancelChannelEdit(sd)" [disabled]="isSaving">
                          <i class="fa-solid fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Add new channel override -->
                <div class="add-channel-override" *ngIf="getChannelsWithoutOverride(sd).length > 0">
                  <span class="add-label">Add override for:</span>
                  <div class="channel-buttons">
                    <button class="add-channel-btn"
                            *ngFor="let channel of getChannelsWithoutOverride(sd)"
                            (click)="startChannelEdit(sd, channel.ID)"
                            [disabled]="isSaving || sd.editingChannelId !== null">
                      <i class="fa-solid fa-plus"></i> {{ channel.Name }}
                    </button>
                  </div>
                </div>

                <!-- New channel override edit panel -->
                <div class="new-channel-override" *ngIf="isEditingNewChannel(sd)">
                  <div class="new-override-header">
                    <i class="fa-solid fa-broadcast-tower"></i>
                    <span>{{ getEditingChannelName(sd) }}</span>
                  </div>
                  <div class="new-override-editor">
                    <!-- Boolean -->
                    <div *ngIf="isBoolean(sd)" class="boolean-new-override">
                      <label class="toggle-switch">
                        <input type="checkbox"
                               [checked]="sd.channelEditValue?.toLowerCase() === 'true'"
                               (change)="sd.channelEditValue = ($any($event.target).checked).toString()"
                               [disabled]="isSaving" />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="value-label">{{ sd.channelEditValue?.toLowerCase() === 'true' ? 'Enabled' : 'Disabled' }}</span>
                    </div>
                    <!-- Number -->
                    <input *ngIf="isNumber(sd)" type="number" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" />
                    <!-- String -->
                    <input *ngIf="!isBoolean(sd) && !isNumber(sd) && !isRichType(sd)" type="text" [(ngModel)]="sd.channelEditValue" class="channel-edit-input" placeholder="Enter value..." />
                    <!-- Rich types -->
                    <mj-code-editor *ngIf="isRichType(sd)"
                                   [(ngModel)]="sd.channelEditValue"
                                   [language]="isJSON(sd) ? 'json' : 'markdown'"
                                   [lineWrapping]="true"
                                   class="channel-rich-editor"></mj-code-editor>
                  </div>
                  <div class="new-override-actions">
                    <button class="save-btn" (click)="saveChannelOverride(sd, sd.editingChannelId!)" [disabled]="isSaving">
                      <i class="fa-solid fa-check"></i> Save
                    </button>
                    <button class="cancel-btn" (click)="cancelChannelEdit(sd)" [disabled]="isSaving">
                      <i class="fa-solid fa-times"></i> Cancel
                    </button>
                  </div>
                </div>

                <!-- All channels have overrides message -->
                <div class="all-channels-configured" *ngIf="getChannelsWithoutOverride(sd).length === 0 && sd.channelOverrides.length > 0">
                  <i class="fa-solid fa-check-circle"></i>
                  <span>All channels have custom values</span>
                </div>
              </ng-container>
            </div>
          </div>
        </div>
      </div>

      <!-- No Category Selected -->
      <div class="no-selection" *ngIf="!selectedCategory && categoryTree.length > 0">
        <i class="fa-solid fa-hand-pointer"></i>
        <p>Select a category tab to view settings</p>
      </div>

      <!-- No Categories -->
      <div class="no-selection" *ngIf="categoryTree.length === 0 && !isLoading">
        <i class="fa-solid fa-folder-open"></i>
        <p>No setting categories configured</p>
      </div>
    </div>
  </div>
</div>

<!-- Confirm Dialog -->
<div class="confirm-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
  <div class="confirm-dialog" (click)="$event.stopPropagation()">
    <div class="confirm-header">
      <i class="fa-solid fa-question-circle"></i>
      <h3>{{ confirmDialogTitle }}</h3>
    </div>
    <div class="confirm-body">
      <p>{{ confirmDialogMessage }}</p>
    </div>
    <div class="confirm-actions">
      <button class="confirm-btn-yes" (click)="confirmDialogYes()" [disabled]="isSaving">
        <i class="fa-solid fa-check"></i> Yes
      </button>
      <button class="confirm-btn-no" (click)="closeConfirmDialog()" [disabled]="isSaving">
        <i class="fa-solid fa-times"></i> No
      </button>
    </div>
  </div>
</div>
